import config from '@config';
import { AudienceType, Log, Metric } from '@enums';
import AudienceIcon from '@icons/audience.svg?react';
import GWIAudienceIcon from '@icons/audience-gwi.svg?react';
import SharedAudienceIcon from '@icons/audience-shared.svg?react';
import InstantAudienceIcon from '@icons/audience-sparks.svg?react';
import { clearCookiesAndSignOut } from '@platform2-lib/cookies';
import { redirectToLogin } from '@platform2-lib/sso';
import domtoimage from 'dom-to-image-more';
import { type CSSProperties, createElement } from 'react';
import type {
  AudienceOverviewTag,
  InsightStatsItem,
  MessageHistory,
  QuestionPanelProps,
  SelectionMode
} from 'types/common';
import type { ChatListItem, PromptUsage } from 'types/schema';
import { v5 as uuid } from 'uuid';
import 'urlpattern-polyfill';
import { pushPath } from './navigation';

/**
 * Logs a message to the console with various styling options.
 *
 * @param message - The message to log
 * @param props - Configuration options for the log
 * @param props.background - Background color for the log
 * @param props.color - Forced color for the log
 * @param props.custom - Custom array of values to log directly
 * @param props.data - Additional data to log after the message
 * @param props.type - The type of log (affects styling and tag)
 * @param props.id - An identifier for the log message
 *
 * @example
 * // Simple info log
 * log('Hello world');
 *
 * @example
 * // Log with custom styling
 * log('Important message', { type: Log.Notice });
 *
 * @example
 * // Log with additional data
 * log('User details', { data: { name: 'John', age: 30 } });
 *
 * @example
 * // Log with custom elements
 * log('Custom log', { custom: ['value1', { obj: 'value2' }] });
 */
export function log(
  message: string,
  props:
    | Log
    | string
    | {
        background?: string;
        color?: string;
        custom?: (string | object)[];
        data?: string | object;
        type?: Log;
        id?: string;
      } = Log.Info
) {
  let {
    background,
    color: forcedColor,
    custom,
    data,
    id,
    type
  } = typeof props === 'object' ? props : {};

  if (custom) {
    console.info(...[message, ...custom]);
    return;
  }

  const { color, tag } = (() => {
    switch (type || props.toString()) {
      case Log.Notice:
        return { color: 'darkgoldenrod', tag: 'Important' };
      case Log.Warning:
        return { color: 'darkorange', tag: '🚨' };
      case Log.Debug:
        return { color: 'darkmagenta', tag: '🚧' };
      default:
        return { color: '#2563eb', tag: 'i' };
    }
  })();

  const args: (string | object)[] = [
    background ? '%s' : `[${tag}] %s`,
    background
      ? `background: ${background}; color: white; line-height: 16px`
      : `color: ${forcedColor || color}`,
    message
  ];

  const _id = id || (typeof props === 'string' ? props : '');

  if (_id.includes('-')) {
    args[0] += ` (%s)`;
    args.push(_id.split('-').shift());
  }

  if (data) {
    args.push(data);
  }

  if (background) {
    args[0] = ` ${args[0]} `;
  }

  args[0] = `%c${args[0]}`;

  console.info(...args);
}

/**
 * Decodes a JWT token into its header, payload, and signature components.
 *
 * @param token - The JWT token to decode
 * @returns An object containing the decoded header, payload, and signature
 * @throws An error if the JWT is invalid
 */
export function decodeJwt(token: string) {
  const [header, payload, signature] = token.split('.');

  if (!(header && payload)) {
    throw new Error('Invalid JWT: must have at least two parts');
  }

  const decodeBase64Url = (str: string) =>
    decodeURIComponent(
      atob(str.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

  try {
    return {
      header: JSON.parse(decodeBase64Url(header)),
      payload: JSON.parse(decodeBase64Url(payload)),
      signature: signature // Still base64url encoded
    };
  } catch (error) {
    throw new Error('Failed to decode JWT: ' + error.message);
  }
}

/**
 * Extracts the chat ID from the current URL pathname.
 *
 * @returns The chat ID if present in the URL pattern, or null if not found
 *
 * @example
 * // URL: https://example.com/chat/123
 * const chatId = getPathnameId(); // returns "123"
 *
 * @example
 * // URL: https://example.com/dashboard
 * const chatId = getPathnameId(); // returns null
 */
export function getPathnameId() {
  const pattern = new URLPattern({ pathname: '*/chat/:id' });
  const result = pattern.exec(window.location.href);

  return result?.pathname.groups.id || null;
}

/**
 * Updates the current URL pathname to include a new chat ID.
 *
 * @param base - The base URL to use for the update
 * @param id - The new chat ID to set in the URL
 *
 * @example
 * // Updates the URL to: https://example.com/chat/123
 * updatePathname('https://example.com', '123');
 */
export function updatePathname(base: string, id: string) {
  window.sessionStorage.setItem(
    'octopus.popstate_event.ignore_duplicate',
    'true'
  );

  pushPath(`${base}/chat/${id}`);
  log('Updated the URL with the new chat ID.', { type: Log.Notice, id });
}

/**
 * Converts pixel values to rem units based on a specified base font size.
 *
 * @param pxValue - The pixel value to convert
 * @param baseFontSize - The base font size in pixels (defaults to 16)
 * @returns A string with the converted value in rem units
 *
 * @example
 * // Convert 24px to rem with default base font size (16px)
 * const padding = rem(24); // returns "1.5rem"
 *
 * @example
 * // Convert 20px to rem with custom base font size (10px)
 * const fontSize = rem(20, 10); // returns "2rem"
 */
export function rem(pxValue: number, baseFontSize = 16) {
  return `${pxValue / baseFontSize}rem`;
}

export function generateKey(name: string) {
  // Pre-defined namespace
  const namespace = 'e7d2318a-e56d-44f0-bfde-01f1bc86df9d';

  return uuid(name, namespace);
}

/**
 * Logs out the current user.
 *
 * This function clears all cookies and redirects to the login page.
 *
 * @example
 * // Log out user
 * logOut();
 */
export function logOut() {
  clearCookiesAndSignOut(config.env);
}

/**
 * Highlights specific terms in text by wrapping them in HTML elements.
 *
 * @param text - The text string to process
 * @param terms - Terms to highlight
 * @param marker - HTML element tag to wrap terms ('b', 'i', 'q' or null)
 * @param style - CSS styles to apply to highlighted terms
 * @returns Array of React elements with highlighted terms
 *
 * @example
 * // Returns elements with "Hello" highlighted in red
 * highlightTerms("Hello world", ["Hello"], "b", { color: "red" });
 */
export function highlightTerms(
  text: string,
  terms: string[],
  marker?: 'b' | 'i' | 'q' | null,
  style?: CSSProperties
) {
  let sanitizedText = text.replace(/'/g, '');

  for (let i = 0; i < terms.length; i++) {
    // Delimit each term within the text
    sanitizedText = sanitizedText.replace(terms[i], `✂️${terms[i]}✂️`);
  }

  return (
    sanitizedText
      // Split the delimited text into segments
      .split('✂️')
      // Create the corresponding JSX elements
      .map((segment, index) => {
        const key = generateKey(`${segment}.${index}`);
        const isTerm = terms.includes(segment);
        const props = {
          key,
          style: isTerm ? style : undefined
        };

        return createElement(
          isTerm && marker ? marker : 'span',
          props,
          segment
        );
      })
  );
}

/**
 * Formats the title of a chat, appending date/time information if needed.
 *
 * @param chat - The chat item whose title needs formatting
 * @returns A formatted title string that includes either the original title or
 *          a fallback with timestamp information
 *
 * @example
 * // Returns "My Chat Title"
 * formatChatTitle({
 *   id: '123',
 *   title: 'My Chat Title',
 *   created_at: '2023-01-01'
 * });
 *
 * @example
 * // Returns "Chat 12345678, 1 Jan 2023, 13:45" for chat with generic title
 * formatChatTitle({
 *   id: '12345678-90ab',
 *   title: 'Chat',
 *   created_at: '2023-01-01T13:45:00Z'
 * });
 *
 * @example
 * // Returns "Untitled chat, 1 Jan 2023, 13:45" for chat with no title
 * formatChatTitle({
 *   id: '123',
 *   title: '',
 *   created_at: '2023-01-01T13:45:00Z'
 * });
 */
export function formatChatTitle(chat: ChatListItem) {
  const dateTime = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });

  if (!chat.title) {
    return `Untitled chat, ${dateTime.format(Date.now())}`;
  }

  return chat.title === 'Chat'
    ? `Chat ${chat.id.substring(0, 8)}, ${dateTime.format(
        new Date(chat['created_at'])
      )}`
    : chat.title;
}

/**
 * Generates a human-readable text representing the remaining quota status.
 *
 * @param quota - The prompt usage information containing details about remaining
 *                prompts and reset time.
 * @param compact - Optional flag to generate a more compact version of the
 *                  status text. When true, omits the word "left" and uses
 *                  shorter month format.
 *
 * @returns A formatted string displaying the remaining prompts and, if available,
 *          the reset date.
 *          Examples:
 *          - "10 prompts left until 15 January"
 *          - "10 prompts until 15 Jan" (compact)
 *          - "No prompts left"
 *
 * @example
 * // Returns "5 prompts left until 20 January"
 * getQuotaStatusText({
 *   usage_remaining: 5,
 *   reset_at: "2023-01-20T00:00:00Z"
 * });
 *
 * @example
 * // Returns "5 prompts until 20 Jan"
 * getQuotaStatusText(
 *   { usage_remaining: 5, reset_at: "2023-01-20T00:00:00Z" },
 *   true
 * );
 */
export function getQuotaStatusText(quota: PromptUsage) {
  const statusText = `${quota['usage_remaining'] || 'No'} prompts left`;

  if (!quota['reset_at']) {
    return statusText;
  }

  const timestamp = new Date(quota['reset_at']).getTime();

  if (Number.isNaN(timestamp) || timestamp < Date.now()) {
    return statusText;
  }

  let monthFormat: Intl.DateTimeFormatOptions['month'] = 'long';

  if (window.innerWidth <= config.breakpoints.xSmall) {
    monthFormat = undefined;
  } else if (window.innerWidth < config.breakpoints.small) {
    monthFormat = 'short';
  }

  if (!monthFormat) {
    return statusText;
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: monthFormat
  });

  return `${statusText} until ${formatter.format(timestamp)}`;
}

/**
 * Converts a hex color code to an RGBA color value with the specified alpha.
 *
 * @param hex - The hexadecimal color code (e.g., "#FF0000" for red)
 * @param alpha - The alpha value between 0 and 1 (e.g., 0.5 for 50% opacity)
 * @returns A formatted RGBA color string in hexadecimal format
 *
 * @example
 * // Returns "#FF000080" (red with 50% opacity)
 * rgba("#FF0000", 0.5);
 *
 * @example
 * // Returns "#0000FF33" (blue with 20% opacity)
 * rgba("#0000FF", 0.2);
 */
export function rgba(hex: string, alpha: number) {
  const alphaHex = Math.floor(alpha * 100)
    .toString(16)
    .padStart(2, '0');

  return `${hex}${alphaHex}`.toUpperCase();
}

/**
 * Generates a string of CSS class names from either an array of class names or
 * a record of class name to boolean mappings.
 *
 * @param classNames - Either an array of class name strings or an object where
 *                   keys are class names and values are booleans indicating
 *                   inclusion
 * @returns A space-separated string of class names
 *
 * @example
 * // Using an array of class names
 * getClassName(['btn', 'btn-primary']); // returns "btn btn-primary"
 *
 * @example
 * // Using a record with conditional classes
 * getClassName({
 *   'btn': true,
 *   'btn-primary': true,
 *   'disabled': false
 * }); // returns "btn btn-primary"
 */
export function getClassName(classNames: Record<string, boolean> | string[]) {
  if (Array.isArray(classNames)) {
    return classNames.filter(Boolean).join(' ');
  }

  return Object.entries(classNames)
    .filter(([, value]) => value)
    .map(([className]) => className)
    .join(' ');
}

/**
 * Selects a random subset of examples from an array of strings.
 *
 * @param examples - An array of example strings to choose from
 * @returns A subset of the input examples, randomly selected
 *
 * @example
 * // Returns a random subset of 3 examples (if config.prompt.examples.size = 3)
 * getRandomExamples(['Example 1', 'Example 2', 'Example 3', 'Example 4',
 *                   'Example 5']);
 */
export function getRandomExamples(examples: string[]) {
  let start = Math.floor(Math.random() * examples.length);

  if (start + config.prompt.examples.size >= examples.length) {
    start = examples.length - config.prompt.examples.size;
  }

  return examples.slice(start, start + config.prompt.examples.size);
}

/**
 * Returns the current time formatted according to the en-GB locale.
 *
 * @returns A string representing the current time in the format "HH:MM"
 *
 * @example
 * // Returns something like "14:30"
 * const time = getCurrentTime();
 */
export function getCurrentTime() {
  const dateTime = new Date();
  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: 'numeric'
  });

  return formatter.format(dateTime);
}

/**
 * Returns metadata for an audience type.
 *
 * @param type - The type of audience
 * @returns An object containing the icon and description for the audience type
 */
export const getAudienceMetadata = (type: AudienceType) => {
  switch (type) {
    case AudienceType.GWIAudience:
      return {
        icon: <GWIAudienceIcon className="icon" />,
        description: 'GWI Audience'
      };
    case AudienceType.SharedAudience:
      return {
        icon: <SharedAudienceIcon className="icon" />,
        description: 'Shared Audience'
      };
    case AudienceType.InstantAudience:
      return {
        icon: <InstantAudienceIcon className="icon" />,
        description: 'AI Audience'
      };
    default: // Authored audience
      return {
        icon: <AudienceIcon className="icon" />,
        description: 'My Audience'
      };
  }
};

/**
 * Retrieves an item from a storage object (localStorage or sessionStorage).
 *
 * @param key - The key of the item to retrieve
 * @param storage - The storage object to use (defaults to localStorage)
 * @returns The parsed item if it's a JSON string, otherwise the raw string
 */
export function getStorageItem<T = string>(
  key: string,
  storage: Storage = window.localStorage
): T | null {
  try {
    return JSON.parse(storage.getItem(key) || 'null') as T | null;
  } catch {
    return (storage.getItem(key) || null) as T | null;
  }
}

/**
 * Removes Markdown formatting from a string, returning only the plain text.
 *
 * This function strips out code blocks, inline code, images, links (but keeps
 * the link text), bold and italic formatting, blockquotes, headings, horizontal
 * rules, strikethroughs, unordered and ordered list markers, and trims extra
 * spaces at the start and end of lines.
 *
 * @param text - The Markdown-formatted string to be stripped.
 * @returns The plain text string with all Markdown formatting removed.
 *
 * @example
 * // Returns "Hello world"
 * stripMarkdown("**Hello** _world_");
 *
 * @example
 * // Returns "This is a link"
 * stripMarkdown("[This is a link](https://example.com)");
 */
export function stripMarkdown(text: string) {
  let output = text;
  // Remove code blocks
  // output = text.replace(/```[\s\S]*?```/g, '');
  // Remove inline code
  // output = output.replace(/`([^`]+)`/g, '$1');
  // Remove images ![alt](url)
  // output = output.replace(/!\[.*?\]\(.*?\)/g, '');
  // Remove links but keep the text [text](url)
  output = output.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Remove bold and italic (**text**, *text*, __text__, _text_)
  // output = output.replace(/(\*\*|__)(.*?)\1/g, '$2');
  // output = output.replace(/(\*|_)(.*?)\1/g, '$2');
  // Remove blockquotes
  // output = output.replace(/^\s{0,3}>\s?/gm, '');
  // Remove headings
  // output = output.replace(/^\s{0,3}#{1,6}\s?/gm, '');
  // Remove horizontal rules
  // output = output.replace(/^\s{0,3}([-*_]\s?){3,}$/gm, '');
  // Remove strikethrough
  // output = output.replace(/~~(.*?)~~/g, '$1');
  // Remove unordered list markers
  // output = output.replace(/^\s*[-+*]\s+/gm, '');
  // Remove ordered list markers
  // output = output.replace(/^\s*\d+\.\s+/gm, '');
  // Remove extra spaces at start/end of lines
  // output = output.replace(/^\s+|\s+$/gm, '');
  return output;
}

/**
 * Exports an element as an image.
 *
 * @param element - The element to export
 * @param chartType - The type of chart to export
 * @param filename - The filename to use for the exported image
 *
 * @example
 * // Exports an element as an image
 * exportAsImage(element, 'regular', 'chart');
 */
export async function exportAsImage(
  element: HTMLElement,
  chartType: 'regular' | 'compact',
  filename: string
) {
  // Calculate scale to achieve target width
  const currentWidth = element.offsetWidth;
  const targetWidth = config.chart.imageExport[chartType].width;
  const scale = (targetWidth / currentWidth) * 2;

  // Generate PNG with dom-to-image-more
  const dataUrl = await domtoimage.toPng(element, {
    width: element.offsetWidth * scale,
    height: element.offsetHeight * scale,
    style: {
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      width: `${element.offsetWidth}px`,
      height: `${element.offsetHeight}px`
    }
  });

  // Download the image
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = dataUrl;
  link.click();
}

/**
 * Auto adjusts the height of a textarea to fit the content.
 *
 * @param textArea - The textarea element to adjust the height of
 * @param lineHeight - The line height of the textarea
 * @returns The height of the textarea in pixels
 *
 * @example
 * // Auto adjusts the height of a textarea to fit the content
 * const textarea = document.getElementById('textarea');
 * if (textarea) {
 *   autoAdjustTextAreaHeight(textarea as HTMLTextAreaElement, 24);
 * }
 */
export function autoAdjustTextAreaHeight(
  textArea: HTMLTextAreaElement,
  lineHeight: number
) {
  textArea.style.setProperty('height', 'auto');

  if (textArea.value.trim() === '') {
    return;
  }

  let totalLines = textArea.scrollHeight / lineHeight;

  if (totalLines > config.prompt.textArea.maxLines) {
    totalLines = config.prompt.textArea.maxLines;
  }

  const textAreaHeight = totalLines * lineHeight;

  textArea.style.setProperty('height', 'auto');
  textArea.style.setProperty('height', `${textAreaHeight}px`);
}

/**
 * Counts the number of sources from a given text by extracting all numeric
 * values that match the pattern following 'sources?total='.
 *
 * @param text - The string to search for source totals.
 * @returns The sum of all found source totals as a number.
 *
 * @example
 * // Returns 3 for input containing 'sources?total=1' and 'sources?total=2'
 * countSources(
 *   "Some answer... sources?total=1 ... sources?total=2"
 * );
 */
export function countSources(text: string) {
  return (text.match(/(?<=sources\?total=)\d+/g) || [])
    .map(Number)
    .reduce((sum, num) => sum + num, 0);
}

/**
 * Counts the number of source links from a given text.
 *
 * @param text - The string to search for source links.
 * @returns The number of source links in the string.
 *
 * @example
 * countSourceLinks("Some answer... spark://sources? ...");
 * // Returns n for input containing 'spark://sources?'
 */
export function countSourceLinks(text: string) {
  return (text.match(/spark:\/\/sources\?/g) || []).length;
}

export function formatMetricValue(value: number, metric?: Metric): string {
  if (metric === Metric.Index) {
    return `${value.toFixed(1)} IDX`;
  }

  if (metric === Metric.AudiencePercentage) {
    return `${value.toFixed(0)}%`;
  }

  return formatLargeNumber(value);
}

/**
 * Formats a large number into a compact string with M (millions) or B (billions) suffix.
 *
 * @param value - The number to format.
 * @returns A string like "1.2M" or "3.5B".
 */
export function formatLargeNumber(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return value.toFixed(1);
}

/**
 * Parses a `spark://audience-overlap` URL and returns the ordered audience IDs.
 *
 * Expected order: audience A, audience B, audience A AND B.
 */
export function parseAudienceOverlapIds(value: string | undefined): string[] {
  if (!value) {
    return [];
  }

  try {
    const url = new URL(value);

    if (url.protocol !== 'spark:' || url.hostname !== 'audience-overlap') {
      return [];
    }

    const audienceIds = url.searchParams
      .get('audiences')
      ?.split(',')
      .map((id) => id.trim());

    if (
      !audienceIds ||
      audienceIds.length !== 3 ||
      audienceIds.some((id) => !id)
    ) {
      return [];
    }

    return audienceIds;
  } catch {
    return [];
  }
}

/**
 * Parses a `spark://insight-stats` URL into insight stat cards.
 *
 * Expected repeated params per insight: `insightId`, `value`, `title`,
 * `description`, `body` (same keys for every insight, aligned by index).
 */
export function parseInsightStats(
  value: string | undefined
): InsightStatsItem[] | null {
  if (!value) {
    return null;
  }

  try {
    const url = new URL(value);

    if (url.protocol !== 'spark:' || url.hostname !== 'insight-stats') {
      return null;
    }

    const insightIds = url.searchParams.getAll('insightId');

    if (!insightIds.length) {
      return null;
    }

    const titles = url.searchParams.getAll('title');
    const descriptions = url.searchParams.getAll('description');
    const bodies = url.searchParams.getAll('body');
    const values = url.searchParams.getAll('value');

    return insightIds.map((insightId, index) => ({
      insightId,
      value: values[index],
      title: titles[index],
      description: descriptions[index],
      body: bodies[index]
    }));
  } catch {
    return null;
  }
}

/** * Extracts and parses a `spark://question-panel` URL embedded in an agent
 * response string, returning the structured question definitions ready for
 * the question panel component.
 *
 * @param response - The agent's `natural_language_response` string.
 * @returns An array of `QuestionPanelProps`, or `null` if no valid URL is found.
 */
export function parseQuestionPanelUrl(
  response: string | undefined
): QuestionPanelProps[] | null {
  if (!response) {
    return null;
  }

  // Find a URL inside ](...), and capture only the URL part while allowing
  // ")" in option text.
  const rawUrl = response.match(
    /\]\((spark:\/\/question-panel[^\s<>"']+)\)/
  )?.[1];

  if (!rawUrl) {
    return null;
  }

  try {
    const url = new URL(rawUrl);

    if (url.protocol !== 'spark:' || url.hostname !== 'question-panel') {
      return null;
    }

    const prompts = url.searchParams.getAll('prompt');

    if (!prompts.length) return null;

    const selectionModes = url.searchParams.getAll('selection_mode');
    const allowSkips = url.searchParams.getAll('allow_skip');
    const labels = url.searchParams.getAll('label');

    return prompts.map((prompt, i) => ({
      prompt,
      selectionMode: (selectionModes[i] ?? 'single') as SelectionMode,
      allowSkip: allowSkips[i] === 'true',
      label: labels[i] ?? undefined,
      options: url.searchParams.getAll(`option_${i}`)
    }));
  } catch {
    return null;
  }
}

/**
 * Returns question-panel data when the last message is an agent reply
 * containing a `spark://question-panel` link (e.g. after reload or reopen).
 */
export function findPendingQuestionPanel(
  messages: MessageHistory
): QuestionPanelProps[] | null {
  const last = messages.at(-1);

  if (!last || !('natural_language_response' in last)) {
    return null;
  }

  return parseQuestionPanelUrl(last.natural_language_response);
}

/**
 * Parses the `tags` param from a `spark://audience-overview` link into chips
 * shown on the audience confirmation card.
 *
 * @param value - Comma-separated tags in `label:value` format.
 */
export function parseAudienceOverviewTags(
  value: string | null
): AudienceOverviewTag[] {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((tag): AudienceOverviewTag | null => {
      const [rawLabel, rawValue] = tag.split(':');

      const label = rawLabel?.trim().replace(/[_-]+/g, ' ');
      const value = rawValue?.trim();

      if (!label || !value) {
        return null;
      }

      return {
        label,
        // If plain integer convert to locale string "1500000" → "1,500,000"
        value: /^\d+$/.test(value) ? Number(value).toLocaleString() : value
      };
    })
    .filter((tag): tag is AudienceOverviewTag => tag !== null);
}

export {
  flattenAgentLinkChildren,
  getChartType,
  isInlineChart,
  mapUrlToInsightChartsBlockProps
} from './agentLinks';
export {
  DEFAULT_METRIC,
  formatMetricLabel,
  getChartColor,
  getInsightChartMinWidth,
  INSIGHT_MINI_CHART_COLOR_NAMES,
  isSupportedChartType,
  MAX_VALUES,
  resolveBarColors
} from './charts';
export {
  buildChartDataFromInsightChart,
  getDataPointName,
  type InsightSeriesFailure,
  type InsightSeriesResult,
  type InsightSeriesSuccess,
  isOrigin,
  sliceChartData
} from './insightChartSeries.ts';
export {
  isSettingsPath,
  pushPath,
  replacePath,
  syncBrowserPathname
} from './navigation';
